import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, AbstractControlOptions, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ArrayFunctions, SeparatorEnum } from '@campus/utils';
import { combineLatest, EMPTY, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  FieldItemInterface,
  FormFieldDataInterface,
  FormFieldOptionsInterface,
  FormTemplateBuilderConfigInterface,
  FormTemplateInterface,
  FormValueType,
  UpdaterType,
} from './form-template-builder.service.interface';
const { wrapIntoArray } = ArrayFunctions;

@Injectable()
export class FormTemplateBuilderService implements OnDestroy {
  private subscriptions = new Subscription();
  private translationDict = {};

  private updaterMap: Map<AbstractControl, UpdaterType> = new Map();
  public getUpdater(control: AbstractControl): UpdaterType {
    return this.updaterMap.get(control);
  }

  public getFormTemplate(config: FormTemplateBuilderConfigInterface): FormTemplateInterface {
    if (config.translationDict) {
      this.translationDict = config.translationDict;
    }

    const formTemplate: FormTemplateInterface = {};
    config.groups.forEach((group) => {
      const label = group.label || this.getTranslation(group.groupName);

      formTemplate[group.groupName] = { label, controls: {} };

      const { controls = [] } = group;

      controls.forEach((control) => {
        if (!control.hasTranslationDict) control.translationDict?.(this.translationDict);

        const field = this.createFormField(control.formFieldOptions);
        field.name = control.property;

        formTemplate[group.groupName].controls[control.property] = field;
      });
    });
    return formTemplate;
  }

  public getForm(template: FormTemplateInterface, extras: { [name: string]: AbstractControl } = {}): FormGroup {
    const form = new FormGroup({
      ...Object.entries(template).reduce(
        (formSectionAcc, [sectionKey, section]) => ({
          ...formSectionAcc,
          [sectionKey]: new FormGroup(
            Object.entries(section.controls).reduce((acc, [key, field]) => ({ ...acc, [key]: field.control }), {})
          ),
        }),
        {}
      ),
      ...extras,
    });

    return form;
  }

  private createFormField({
    useFormArrayControl = false,
    updater = 'scalar',
    value$ = EMPTY,
    valueLabel$ = EMPTY,
    field,
    reviewField,
    fieldLabel,
    options,
    source$ = EMPTY,
    current$ = EMPTY,
    labelProperty = 'name',
    reviewLabelProperty = 'name',
    excludeCurrentFromSource = false,
    isBoolean = false,
    inputType = 'string',
    hideInBulk = false,
    formArrayControlMapFn,
  }: FormFieldOptionsInterface = {}): FormFieldDataInterface {
    const control = useFormArrayControl
      ? this.createFormArray(options, updater, value$, formArrayControlMapFn)
      : this.createFormControl(options, updater, value$);

    const mappedSource$ = source$.pipe(this.mapToFormFieldSource$(labelProperty));

    const currentValue$ = this.getCurrent$(current$, field, labelProperty, isBoolean);

    const filteredSource$ = this.getFilteredSource$(excludeCurrentFromSource, mappedSource$, currentValue$);

    const label$ = valueLabel$ === EMPTY ? this.getLabel$(currentValue$) : valueLabel$;

    let reviewLabel$ = label$;
    if (reviewField) {
      const currentReview$: Observable<FieldItemInterface | FieldItemInterface[]> = this.getCurrent$(
        current$,
        reviewField,
        reviewLabelProperty,
        isBoolean
      );
      reviewLabel$ = this.getLabel$(currentReview$, SeparatorEnum.NEWLINE);
    }

    return {
      control,
      source$: filteredSource$,
      label$,
      reviewLabel$,
      current$: currentValue$,
      fieldLabel,
      inputType,
      hideInBulk,
    };
  }

  private getInitialValue(value$: Observable<FormValueType>): FormValueType {
    let initialValue;
    value$.pipe(take(1)).subscribe((value) => {
      initialValue = value;
    });
    return initialValue;
  }

  private createFormControl(
    options: AbstractControlOptions,
    updater: UpdaterType,
    value$: Observable<FormValueType>
  ): FormControl {
    const initialValue = this.getInitialValue(value$);

    const formControl = new FormControl(initialValue, options);
    this.updaterMap.set(formControl, updater);

    this.subscriptions.add(
      value$.subscribe((value) => {
        formControl.setValue(value, { emitEvent: false });
        formControl.markAsPristine();
      })
    );

    return formControl;
  }

  private createFormArray(
    options: AbstractControlOptions,
    updater: UpdaterType,
    value$: Observable<FormValueType>,
    formArrayControlMapFn?: (item) => AbstractControl
  ): FormArray {
    const formArrayOptions = { updateOn: 'change', ...options } as AbstractControlOptions;
    const formArray = new FormArray([], formArrayOptions);

    const intialArrayValue = (this.getInitialValue(value$) as object[]) || [];
    const actualInitialArrayValue = Array.isArray(intialArrayValue) ? intialArrayValue : [intialArrayValue];

    actualInitialArrayValue.forEach((value) => {
      const formControl = formArrayControlMapFn ? formArrayControlMapFn(value) : new FormControl(value);

      formArray.push(formControl);
    });

    this.updaterMap.set(formArray, updater);

    this.subscriptions.add(
      value$.subscribe((value) => {
        value = value || [];

        _updateControlArrayLength(formArray, value as object[]);
        formArray.reset(value, { emitEvent: false });
      })
    );

    return formArray;

    function _updateControlArrayLength(fArray: FormArray, newValue: unknown[]) {
      const controls = fArray.controls;
      const controlsLength = controls.length;
      const newValueLength = newValue.length;

      if (controlsLength > newValueLength) {
        for (let i = controlsLength - 1; i >= newValueLength; i--) {
          fArray.removeAt(i);
        }
        return;
      }

      if (controlsLength < newValueLength) {
        for (let i = controlsLength; i < newValueLength; i++) {
          fArray.push(new FormControl(newValue[i]), { emitEvent: false });
        }
        return;
      }
    }
  }

  private getCurrent$(
    currentValue$: Observable<any>,
    property: string,
    labelProperty = 'name',
    isBoolean = false
  ): Observable<FieldItemInterface | FieldItemInterface[]> {
    return currentValue$.pipe(
      map((value) => {
        const val = value[property];

        if (val || isBoolean) {
          return _mapToFieldItem(val);
        }
      })
    );

    function _mapToFieldItem(
      value: string | { id: number; value: string } | { id: number; value: string }[]
    ): FieldItemInterface | FieldItemInterface[] {
      if (Array.isArray(value)) {
        return value.map((item) => _mapToFieldItem(item)) as FieldItemInterface[];
      }
      if (value && Object.hasOwnProperty.call(value, 'id')) {
        return { id: value['id'], label: value[labelProperty]?.toString() };
      }
      return {
        label: isBoolean ? (value ? 'ja' : 'nee') : value?.toString(),
      };
    }
  }

  private getLabel$(
    current$: Observable<FieldItemInterface | FieldItemInterface[]>,
    separator: SeparatorEnum = SeparatorEnum.COMMA
  ): Observable<string> {
    return current$.pipe(
      map((value: FieldItemInterface[]) => {
        const _value = wrapIntoArray(value);
        return _value.map((item) => item?.label).join(separator);
      })
    );
  }

  private getFilteredSource$(
    excludeCurrentFromSource: boolean,
    source$: Observable<FieldItemInterface[]>,
    current$: Observable<FieldItemInterface | FieldItemInterface[]>
  ): Observable<FieldItemInterface[]> {
    if (!excludeCurrentFromSource) return source$;

    return combineLatest([source$, current$]).pipe(
      map(([source, current]) => {
        if (!Array.isArray(current)) return source;
        const currentIds = new Set(current.map((item) => item.id));
        return source.filter((item) => !currentIds.has(item.id));
      })
    );
  }

  private mapToFormFieldSource$<T extends { id?: string | number }>(
    labelProperty = 'name'
  ): (source$: Observable<T[]>) => Observable<FieldItemInterface[]> {
    return map((items: T[]) =>
      wrapIntoArray(items).map((item) => ({
        id: item.id,
        label: item[labelProperty]?.toString(),
      }))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getTranslation(key: string, fallback?: string): string {
    return this.translationDict[key] || fallback || key;
  }
}
